/* global axios */
import ApiClient from '../ApiClient';

// Viewer's UTC offset in hours, matching the reports API convention so the
// backend can anchor calendar ranges to the viewer's day.
const getTimezoneOffset = () => -new Date().getTimezoneOffset() / 60;

class CaptainAssistant extends ApiClient {
  constructor() {
    super('captain/assistants', { accountScoped: true });
  }

  get({ page = 1, searchKey } = {}) {
    return axios.get(this.url, {
      params: {
        page,
        searchKey,
      },
    });
  }

  create(data) {
    if (data instanceof FormData) {
      return axios.post(this.url, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    }
    if (data && data.avatar instanceof File) {
      const formData = new FormData();
      formData.append('assistant[name]', data.name || '');
      formData.append('assistant[description]', data.description || '');
      formData.append('assistant[avatar]', data.avatar);
      if (data.config) {
        Object.entries(data.config).forEach(([k, v]) => {
          formData.append(`assistant[config][${k}]`, v);
        });
      }
      return axios.post(this.url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    }
    return axios.post(this.url, { assistant: data });
  }

  update(id, data) {
    if (data instanceof FormData) {
      return axios.patch(`${this.url}/${id}`, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    }
    if (data && (data.avatar instanceof File || data.avatar)) {
      const formData = new FormData();
      if (data.name !== undefined) formData.append('assistant[name]', data.name);
      if (data.description !== undefined) formData.append('assistant[description]', data.description);
      if (data.avatar instanceof File) {
        formData.append('assistant[avatar]', data.avatar);
      }
      if (data.config) {
        Object.entries(data.config).forEach(([k, v]) => {
          formData.append(`assistant[config][${k}]`, v);
        });
      }
      if (data.response_guidelines && Array.isArray(data.response_guidelines)) {
        data.response_guidelines.forEach(g => formData.append('assistant[response_guidelines][]', g));
      }
      if (data.guardrails && Array.isArray(data.guardrails)) {
        data.guardrails.forEach(g => formData.append('assistant[guardrails][]', g));
      }
      return axios.patch(`${this.url}/${id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    }
    return axios.patch(`${this.url}/${id}`, { assistant: data });
  }

  deleteAvatar(assistantId) {
    return axios.delete(`${this.url}/${assistantId}/avatar`);
  }

  playground({ assistantId, messageContent, messageHistory }) {
    return axios.post(`${this.url}/${assistantId}/playground`, {
      message_content: messageContent,
      message_history: messageHistory,
    });
  }

  getMetrics({ assistantId, range, signal }) {
    const requestConfig = {
      params: { range, timezone_offset: getTimezoneOffset() },
    };
    if (signal) requestConfig.signal = signal;

    return axios.get(`${this.url}/${assistantId}/metrics`, requestConfig);
  }

  getFaqStats({ assistantId, signal }) {
    const requestConfig = {};
    if (signal) requestConfig.signal = signal;

    return axios.get(`${this.url}/${assistantId}/faq_stats`, requestConfig);
  }

  getSummary({ assistantId, range, stats }) {
    return axios.get(`${this.url}/${assistantId}/summary`, {
      params: { range, timezone_offset: getTimezoneOffset(), stats },
    });
  }

  getDrilldown({ assistantId, metric, range, page, signal }) {
    const requestConfig = {
      params: {
        metric,
        range,
        timezone_offset: getTimezoneOffset(),
        page,
      },
    };
    if (signal) requestConfig.signal = signal;

    return axios.get(`${this.url}/${assistantId}/drilldown`, requestConfig);
  }
}

export default new CaptainAssistant();
